import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, MapPin, Euro, Clock, AlertCircle, Eye, FileText } from 'lucide-react';
import { categoriesService, postsService } from '../../services/api';

const urgencyOptions = [
  { value: 'faible', label: 'Faible' },
  { value: 'normale', label: 'Normale' },
  { value: 'urgente', label: 'Urgente' },
  { value: 'tres_urgente', label: 'Très urgente' }
];

const statusLabels = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-100 text-slate-800' },
  publiee: { label: 'Publiée', color: 'bg-green-100 text-green-800' },
  en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-800' },
  fermee: { label: 'Fermée', color: 'bg-yellow-100 text-yellow-800' },
  archivee: { label: 'Archivée', color: 'bg-slate-100 text-slate-800' }
};

export function MyRequestsPanel() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    categorie: '',
    description: '',
    budget_min: '',
    budget_max: '',
    localisation: '',
    urgence: 'normale',
    date_souhaitee: '',
    status: 'brouillon'
  });

  const fetchData = useCallback(async () => {
    if (!user?.id) {
      setRequests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [requestsRes, categoriesRes] = await Promise.all([
        postsService.getPosts({ type: 'REQUEST', authorId: user.id }),
        categoriesService.getCategories(),
      ]);

      const requestsData = requestsRes?.data ?? requestsRes ?? [];
      setRequests(Array.isArray(requestsData) ? requestsData : []);

      const categoriesData = categoriesRes?.data ?? categoriesRes ?? [];
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Impossible de charger vos demandes');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (authLoading) return;
    fetchData();
  }, [authLoading, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null
      };

      const postData = {
        type: 'REQUEST',
        title: data.titre,
        description: data.description,
        categoryId: data.categorie,
        locationCity: data.localisation,
        status: data.status === 'publiee' ? 'PUBLISHED' : 'DRAFT'
      };

      if (editingRequest) {
        await postsService.updatePost(editingRequest.id, postData);
        toast.success('Demande mise à jour');
      } else {
        await postsService.createPost(postData);
        toast.success('Demande créée');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error?.message || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;

    try {
      await postsService.deletePost(id);
      toast.success('Demande supprimée');
      fetchData();
    } catch (error) {
      toast.error(error?.message || 'Erreur lors de la suppression');
    }
  };

  const openEditDialog = (request) => {
    setEditingRequest(request);
    setFormData({
      titre: request.title ?? request.titre ?? '',
      categorie: request.categoryId ?? request.category?.id ?? request.categorie ?? '',
      description: request.description ?? '',
      budget_min: request.budget_min || '',
      budget_max: request.budget_max || '',
      localisation: request.locationCity ?? request.localisation ?? '',
      urgence: request.urgence ?? 'normale',
      date_souhaitee: request.date_souhaitee || '',
      status: request.status === 'PUBLISHED' ? 'publiee' : request.status === 'DRAFT' ? 'brouillon' : request.status ?? 'brouillon'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingRequest(null);
    setFormData({
      titre: '',
      categorie: '',
      description: '',
      budget_min: '',
      budget_max: '',
      localisation: '',
      urgence: 'normale',
      date_souhaitee: '',
      status: 'brouillon'
    });
  };

  return (
    <div data-testid="documents-tab-demandes">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-600">Gérez vos demandes de travaux publiées sur la plateforme.</p>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-request-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle demande
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingRequest ? 'Modifier la demande' : 'Nouvelle demande'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="request-form">
              <div>
                <Label className="form-label">Titre</Label>
                <Input
                  required
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  placeholder="Ex: Rénovation salle de bain"
                  data-testid="request-titre"
                />
              </div>

              <div>
                <Label className="form-label">Catégorie</Label>
                <Select value={formData.categorie} onValueChange={(v) => setFormData({ ...formData, categorie: v })}>
                  <SelectTrigger data-testid="request-categorie">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id || cat} value={cat.id || cat}>{cat.name || cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="form-label">Description</Label>
                <Textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez votre projet en détail..."
                  data-testid="request-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Budget min (€)</Label>
                  <Input
                    type="number"
                    value={formData.budget_min}
                    onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
                    placeholder="500"
                    data-testid="request-budget-min"
                  />
                </div>
                <div>
                  <Label className="form-label">Budget max (€)</Label>
                  <Input
                    type="number"
                    value={formData.budget_max}
                    onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
                    placeholder="2000"
                    data-testid="request-budget-max"
                  />
                </div>
              </div>

              <div>
                <Label className="form-label">Localisation</Label>
                <Input
                  required
                  value={formData.localisation}
                  onChange={(e) => setFormData({ ...formData, localisation: e.target.value })}
                  placeholder="Paris 15ème"
                  data-testid="request-localisation"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Urgence</Label>
                  <Select value={formData.urgence} onValueChange={(v) => setFormData({ ...formData, urgence: v })}>
                    <SelectTrigger data-testid="request-urgence">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {urgencyOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="form-label">Statut</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger data-testid="request-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="brouillon">Brouillon</SelectItem>
                      <SelectItem value="publiee">Publiée</SelectItem>
                      <SelectItem value="fermee">Fermée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="request-submit">
                  {editingRequest ? 'Mettre à jour' : 'Créer la demande'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="lux-dashboard-card p-6 animate-pulse">
              <div className="h-5 bg-slate-200 rounded w-1/4 mb-4" />
              <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-slate-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : requests.length === 0 ? (
        <div className="lux-dashboard-card p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune demande</h3>
          <p className="text-slate-600 mb-4">Créez votre première demande de travaux</p>
          <Button onClick={() => setDialogOpen(true)}>Créer une demande</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => {
            const statusKey = request.status === 'PUBLISHED' ? 'publiee' : request.status === 'DRAFT' ? 'brouillon' : request.status;
            const title = request.title ?? request.titre;
            const location = request.locationCity ?? request.localisation;
            const category = request.category?.name ?? request.categorie;
            const responsesCount = request._count?.applications ?? request.responses_count ?? 0;
            const viewCount = request.viewCount ?? 0;

            return (
            <div key={request.id} className="lux-dashboard-card p-6" data-testid={`request-item-${request.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {category && <Badge variant="outline">{category}</Badge>}
                  <Badge className={statusLabels[statusKey]?.color ?? statusLabels.brouillon.color}>
                    {statusLabels[statusKey]?.label ?? request.status}
                  </Badge>
                  {request.urgence !== 'normale' && (
                    <Badge className="bg-orange-100 text-orange-800">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {urgencyOptions.find(o => o.value === request.urgence)?.label}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={() => navigate(`/demandes/${request.id}`)} data-testid={`view-${request.id}`} title="Consulter la demande">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(request)} data-testid={`edit-${request.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(request.id)} data-testid={`delete-${request.id}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>

                <button
                  type="button"
                  onClick={() => navigate(`/demandes/${request.id}`)}
                  className="text-left text-lg font-semibold text-slate-900 mb-2 hover:text-[hsl(var(--primary))] transition-colors"
                  data-testid={`view-title-${request.id}`}
                >
                  {title}
                </button>
                <p className="text-slate-600 mb-4 line-clamp-2">{request.description}</p>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-slate-500">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {responsesCount} réponse{responsesCount > 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {viewCount} vue{viewCount > 1 ? 's' : ''}
                    </span>
                  </div>
                  {(request.budget_min || request.budget_max) && (
                    <span className="flex items-center gap-1 font-medium text-slate-900">
                      <Euro className="w-4 h-4" />
                      {request.budget_min && request.budget_max
                        ? `${request.budget_min}€ - ${request.budget_max}€`
                        : request.budget_max ? `Jusqu'à ${request.budget_max}€` : `À partir de ${request.budget_min}€`
                      }
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function MyRequests() {
  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="mb-8">
          <p className="text-sm font-medium text-[hsl(var(--primary))] mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Espace documents
          </p>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Mes demandes</h1>
          <p className="text-slate-600">
            Gérez vos demandes de travaux publiées sur la plateforme.
          </p>
        </div>
        <MyRequestsPanel />
      </div>
    </DashboardLayout>
  );
}
